import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Search } from 'lucide-react';
import ImageCropDetailMediaImage from './ImageCropDetailMediaImage';
import ImageCardMoreAction from '@/pages/ipe/workbench/components/ImageCardMoreAction';
import IpeGalleryDisabledOverlay from '@/pages/ipe/workbench/components/IpeGalleryDisabledOverlay';
import { sanitizeIpeUserMessage } from '@/pages/ipe/workbench/utils/errorHandler';

function GoodsOutputSelectedBadge() {
  return (
    <span className="goods-detail__tile-selected-badge" aria-hidden="true">
      ✓
    </span>
  );
}

function TilePlaceholder({ label, children, muted = true }) {
  return (
    <div className={`img2img-detail__tile img2img-detail__tile--empty${muted ? ' img2img-detail__tile--muted' : ''} ai-image-detail__media-tile`}>
      {label ? <span className="img2img-detail__tile-label">{label}</span> : null}
      <div className="img2img-detail__tile-placeholder">{children}</div>
    </div>
  );
}

/**
 * 图案裁剪详情 · 对齐智能抠图 goods 布局磁贴（ai-image-detail__media-tile）
 */
export default function ImageCropDetailMediaTile({
  label = '',
  url = '',
  displayUrl = '',
  imageId = '',
  alt = '',
  state = 'ready',
  fallbackTip = '',
  selected = false,
  selectable = true,
  selectKey = '',
  disabled = false,
  onRestoreDisabled,
  onToggleSelect,
  onPreview,
  onEdit,
  onDownload,
  imageName = '',
  imageSpaceId = '',
  candidateIds = [],
  navId = '',
  taskId = '',
  outputIndex = null,
  onFavorite,
  onRename,
  favorited = false,
  onRetry,
  retrying = false,
  editOnHover = false,
  cutoutOutputTile = false,
  errorMessage = '',
  onError,
}) {
  const { t } = useTranslation('workbench');
  const raw = String(url || '').trim();
  const hasImage = Boolean(raw || displayUrl);
  const [isHovered, setIsHovered] = useState(false);
  const showEditLabel = editOnHover && isHovered;

  if (state === 'loading') {
    return (
      <TilePlaceholder label={label}>
        <div className="img2img-detail__loading-spinner" aria-hidden="true" />
        <span>{t('studioChrome.processingEllipsis')}</span>
      </TilePlaceholder>
    );
  }

  if (state === 'pending') {
    return (
      <TilePlaceholder label={label}>
        <span>{t('studioChrome.waitingProcess')}</span>
      </TilePlaceholder>
    );
  }

  if (state === 'error') {
    return (
      <TilePlaceholder label={label} muted={false}>
        <span>{t('studioChrome.processFailed')}</span>
        {errorMessage ? (
          <span className="image-crop-detail-media-tile__error-text">
            {sanitizeIpeUserMessage(errorMessage, t('studioChrome.processFailed'))}
          </span>
        ) : null}
        {(typeof onEdit === 'function' || typeof onRetry === 'function') ? (
          <div className="image-crop-detail-media-tile__error-actions">
            {typeof onEdit === 'function' ? (
              <button
                type="button"
                className="image-crop-detail-media-tile__error-btn"
                onClick={onEdit}
              >
                {t('studioChrome.adjust')}
              </button>
            ) : null}
            {typeof onRetry === 'function' ? (
              <button
                type="button"
                className="image-crop-detail-media-tile__error-btn"
                disabled={retrying}
                onClick={onRetry}
              >
                {retrying ? t('studioChrome.retrying') : t('studioChrome.retry')}
              </button>
            ) : null}
          </div>
        ) : null}
      </TilePlaceholder>
    );
  }

  if (!hasImage) {
    return (
      <TilePlaceholder label={label}>
        <span>{t('studioChrome.noImage')}</span>
      </TilePlaceholder>
    );
  }

  const tileClassName = [
    'img2img-detail__tile',
    'img2img-detail__tile--output',
    'ai-image-detail__media-tile',
    selected ? 'is-selected' : '',
    disabled ? 'is-disabled' : '',
    cutoutOutputTile ? 'ai-image-detail__media-tile--cutout-output' : '',
  ].filter(Boolean).join(' ');

  const preview = () => onPreview?.(raw, alt, {
    displayUrl: displayUrl || undefined,
    viewUrl: displayUrl || undefined,
  });

  return (
    <div
      className={tileClassName}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`img2img-detail__tile-frame goods-detail__interactive-frame goods-detail__output-frame ai-image-detail__output-media${selected ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}`}>
        {disabled ? <IpeGalleryDisabledOverlay onRestore={onRestoreDisabled} /> : null}
        {selected ? <GoodsOutputSelectedBadge /> : null}
        {label ? (
          <span className="goods-detail__tile-index-label">{label}</span>
        ) : null}
        <button
          type="button"
          className="img2img-detail__tile-media goods-detail__tile-select-surface"
          onClick={(event) => {
            if (disabled) return;
            if (selectable && onToggleSelect && selectKey) {
              onToggleSelect(selectKey);
              event.currentTarget.blur();
              return;
            }
            preview();
          }}
          aria-label={selectable
            ? (selected ? t('studioChrome.deselectItem', { name: label }) : t('studioChrome.selectItem', { name: label }))
            : t('studioChrome.viewNamed', { label })}
          aria-pressed={selectable ? selected : undefined}
        >
          <ImageCropDetailMediaImage
            url={raw}
            displayUrl={displayUrl}
            imageId={imageId}
            alt={alt}
            onError={onError}
          />
          {fallbackTip ? (
            <span className="image-crop-detail-media-tile__fallback-tip">{fallbackTip}</span>
          ) : null}
        </button>
        <div
          className={`sourcing-product-card__actions${typeof onEdit !== 'function' ? ' sourcing-product-card__actions--dual' : ''}`}
          role="toolbar"
          aria-label={t('studioChrome.actionsFor', { label })}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className="sourcing-product-card__action"
            onClick={preview}
          >
            <Search size={14} aria-hidden="true" />
            {t('studioChrome.listPreview')}
          </button>
          {typeof onEdit === 'function' ? (
            <button
              type="button"
              className={`sourcing-product-card__action${editOnHover ? ' sourcing-product-card__action--detail-hover-edit' : ''}`}
              onClick={() => {
                if (showEditLabel) {
                  onEdit();
                  return;
                }
                preview();
              }}
            >
              <Pencil size={14} aria-hidden="true" />
              {editOnHover ? (
                <>
                  <span className="sourcing-product-card__action-label--detail">{t('studioChrome.detailWord')}</span>
                  <span className="sourcing-product-card__action-label--edit">{t('studioChrome.adjust')}</span>
                </>
              ) : (
                t('studioChrome.adjust')
              )}
            </button>
          ) : null}
          {typeof onDownload === 'function' ? (
            <ImageCardMoreAction
              imageId={String(imageSpaceId || imageId || '').trim()}
              imageUrl={raw || displayUrl}
              imageName={imageName}
              candidateIds={candidateIds}
              navId={navId}
              taskId={taskId}
              outputIndex={outputIndex}
              favorited={favorited}
              onDownload={onDownload}
              onFavorite={onFavorite}
              onRename={onRename}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
